import Chip from "@mui/material/Chip"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../../../common/axios"
import ConditionalLinearProgress from "../../../../../common/components/ConditionalLinearProgress"
import ScrollableTableContainer from "../../../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../../../common/components/tables/TablePaginationRow"
import {
  NoWrapTableCell,
  SortableHeaderCell,
} from "../../../../../common/components/tables/cells"
import {
  usePaginationState,
  useTableSort,
} from "../../../../../common/components/tables/utils"
import { transactionSoldPriceDisplay } from "../../../../../common/utils"

export default function MarketActivityTab({
  marketHistoryTransactions,
  isLoadingMarketHistoryTransactions,
  marketHistoryPageIndex,
  setMarketHistoryPageIndex,
  marketHistoryPageSize,
  setMarketHistoryPageSize,
  marketHistoryTotalResultsSize,
  marketHistorySortColumn,
  marketHistorySortDirection,
  onMarketHistorySortColumnChanged,
}) {
  const formatDate = (date) => {
    return date ? format(new Date(date), "MMM dd, yyyy") : "-"
  }

  return (
    <>
      <ConditionalLinearProgress visible={isLoadingMarketHistoryTransactions} />
      <ScrollableTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeaderCell
                columnName="coalesce_date"
                label="DATE"
                sortColumn={marketHistorySortColumn}
                sortDirection={marketHistorySortDirection}
                onClick={onMarketHistorySortColumnChanged}
              />
              <SortableHeaderCell
                columnName="status"
                label="ACTIVITY"
                sortColumn={marketHistorySortColumn}
                sortDirection={marketHistorySortDirection}
                onClick={onMarketHistorySortColumnChanged}
              />
              <SortableHeaderCell
                columnName="deal_side"
                label="DEAL SIDE"
                sortColumn={marketHistorySortColumn}
                sortDirection={marketHistorySortDirection}
                onClick={onMarketHistorySortColumnChanged}
              />
              <SortableHeaderCell
                columnName="sold_price"
                label="PRICE"
                sortColumn={marketHistorySortColumn}
                sortDirection={marketHistorySortDirection}
                onClick={onMarketHistorySortColumnChanged}
              />
              <SortableHeaderCell
                columnName="address"
                label="ADDRESS"
                sortColumn={marketHistorySortColumn}
                sortDirection={marketHistorySortDirection}
                onClick={onMarketHistorySortColumnChanged}
              />
              <SortableHeaderCell
                columnName="zipcode"
                label="ZIPCODE"
                sortColumn={marketHistorySortColumn}
                sortDirection={marketHistorySortDirection}
                onClick={onMarketHistorySortColumnChanged}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingMarketHistoryTransactions
              ? Array.from({ length: 10 }).map((_, index) => (
                  <TableRow key={index}>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={120} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={140} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="rectangular" width={80} height={24} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="rectangular" width={120} height={24} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={200} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={80} />
                    </NoWrapTableCell>
                  </TableRow>
                ))
              : marketHistoryTransactions.map((row) => (
                  <TableRow key={row.id} hover>
                    <NoWrapTableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(row.coalesce_date)}
                      </Typography>
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.activity}
                      </Typography>
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Chip
                        label={row.deal_side}
                        color={
                          row.deal_side === "List-side" ? "info" : "secondary"
                        }
                      />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primary"
                      >
                        {transactionSoldPriceDisplay(row)}
                      </Typography>
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.address}
                      </Typography>
                    </NoWrapTableCell>
                    <NoWrapTableCell>{row.zipcode}</NoWrapTableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </ScrollableTableContainer>

      <TablePaginationRow
        tableRows={marketHistoryTransactions}
        pageIndex={marketHistoryPageIndex}
        setPageIndex={setMarketHistoryPageIndex}
        pageSize={marketHistoryPageSize}
        setPageSize={setMarketHistoryPageSize}
        totalResultsSize={marketHistoryTotalResultsSize}
        paginationDisabled={isLoadingMarketHistoryTransactions}
      />
    </>
  )
}

export function useMarketActivityTabState(ssotAgentID) {
  const [marketHistoryTransactions, setMarketHistoryTransactions] = useState([])
  const [
    isLoadingMarketHistoryTransactions,
    setIsLoadingMarketHistoryTransactions,
  ] = useState(false)
  const {
    pageIndex: marketHistoryPageIndex,
    setPageIndex: setMarketHistoryPageIndex,
    pageSize: marketHistoryPageSize,
    setPageSize: setMarketHistoryPageSize,
    totalResultsSize: marketHistoryTotalResultsSize,
    setTotalResultsSize: setMarketHistoryTotalResultsSize,
  } = usePaginationState(100)
  const {
    sortColumn: marketHistorySortColumn,
    sortDirection: marketHistorySortDirection,
    onSortColumnChanged: onMarketHistorySortColumnChanged,
    serverSort: marketHistoryServerSort,
  } = useTableSort("coalesce_date", "desc")
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      if (!ssotAgentID) {
        return
      }
      setIsLoadingMarketHistoryTransactions(true)
      axios
        .get(`/realitydb/api/candidate-profile/tx/history/${ssotAgentID}/`, {
          params: {
            page: marketHistoryPageIndex,
            page_size: marketHistoryPageSize,
            ordering: marketHistoryServerSort,
          },
        })
        .then(({ data }) => {
          setMarketHistoryTransactions(data.results)
          setMarketHistoryTotalResultsSize(data.count)
        })
        .finally(() => setIsLoadingMarketHistoryTransactions(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ssotAgentID,
      marketHistoryPageIndex,
      marketHistoryPageSize,
      marketHistoryServerSort,
    ]
  )

  return {
    marketHistoryTransactions,
    isLoadingMarketHistoryTransactions,
    marketHistoryPageIndex,
    setMarketHistoryPageIndex,
    marketHistoryPageSize,
    setMarketHistoryPageSize,
    marketHistoryTotalResultsSize,
    marketHistorySortColumn,
    marketHistorySortDirection,
    onMarketHistorySortColumnChanged,
  }
}
